import { useState, useRef, useEffect } from 'react';
import { Tooltip, Button, Form, Dropdown, OverlayTrigger } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'font-awesome/css/font-awesome.min.css';
import {
  faClipboardCheck,
  faEllipsis,
  faFileExport,
  faFolderPlus,
  faPaperPlane,
  faPaste,
  faShare,
  faTrashCan,
  faUserGraduate,
  faLink
} from '@fortawesome/free-solid-svg-icons';
import AssignMenuModal from '../components/assignmentFloatingMenu/modals/AssignMenuModal';
import MoveMenuModal from './assignmentFloatingMenu/modals/MoveMenuModal';
import ShareMenuModal from './assignmentFloatingMenu/modals/ShareMenuModal';
import PublishMenuModal from './assignmentFloatingMenu/modals/PublishMenuModal';
import SubmitMenuModal from './assignmentFloatingMenu/modals/SubmitMenuModal';
import DuplicateMenuModal from './assignmentFloatingMenu/modals/DuplicateMenuModal';
import DeleteMenuModal from './assignmentFloatingMenu/modals/DeleteMenuModal';
import { useAssignmentContext } from '../context';
import CustomToast from '../../../components/Toast';
import Modal from '../../../components/Modal';
import { useExportXmlAssignmentsMutation, useValidateSubmitAssignmentMutation } from '../../../features/authentication/assignApiSlice';
import { useSaveAssignmentMethodMutation } from '../../../features/authentication/assignmentApislice';
import { DashboardProvider, useDashboardContext } from '../../dashboard/context';
import assignMethodImg from '../../../assets/images/personWhiteboard.png';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import ActivityTypes from '../../../enums/activityTypes';
import useFloatingMenuVisibility from '../../../components/floatingMenu/useFloatingMenuVisibility';

const AssignmentFloatingMenu = () => {
  const [exportAssignment] = useExportXmlAssignmentsMutation();
  const [validateSubmitAssignment] = useValidateSubmitAssignmentMutation();
  const [assignAssignmentsModal, setAssignAssignmentsModal] = useState();
  const [moveAssignmentsModal, setMoveAssignmentsModal] = useState();
  const [shareAssignmentsModal, setShareAssignmentsModal] = useState();
  const [publishAssignmentsModal, setPublishAssignmentsModal] = useState();
  const [submitAssignmentsModal, setSubmitAssignmentsModal] = useState();
  const [duplicateAssignmentsModal, setDuplicateAssignmentsModal] = useState();
  const [deleteAssignmentsModal, setDeleteAssignmentsModal] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const getData = useDashboardContext();
  const selectedAssignment = useAssignmentContext();
  const [saveAssignmentMethod, saveAssignmentMethodResults] = useSaveAssignmentMethodMutation();
  const assignmentMethodRef = useRef(1);
  const [showAssignmentMethodModal, setShowAssignmentMethodModal] = useState(false);
  const [enableCreateLink, setEnableCreateLink] = useState(false);
  const [loadingMenuItemId, setLoadingMenuItemId] = useState(null);
  const {
    filters,
    setAssignedData,
    setAssignedViaUpdate
  } = useAssignmentContext();

  useEffect(() => {
    const assignedData = selectedAssignment?.assignedData ?? [];
    const enableCreateLink = assignedData.length > 0 && (assignedData.every(item => item.assignedVia === 2) || assignedData.every(item => item.assignedVia === 3));
    setEnableCreateLink(enableCreateLink);
  }, [selectedAssignment?.assignedData]);

  const floatingMenu = [
    {
      id: 1,
      name: enableCreateLink ? 'Create Link' : 'Assign',
      tooltip: enableCreateLink ? 'Create assignment link to share' : 'Assign assignments to multiple students and/or yourself',
      icon: <FontAwesomeIcon icon={enableCreateLink ? faLink : faUserGraduate} />,
      modal: () => handleAssignAssignment()
    },
    {
      id: 2,
      name: 'Move',
      tooltip: 'Move assignments to another folder',
      icon: <FontAwesomeIcon icon={faFolderPlus} />,
      modal: () => setMoveAssignmentsModal(!moveAssignmentsModal)
    },
    {
      id: 3,
      name: 'Export',
      tooltip: 'Export assignments for sharing',
      icon: <FontAwesomeIcon icon={faFileExport} />
    },
    {
      id: 4,
      name: 'Send Copy',
      tooltip: 'Share assignments with other teachers',
      icon: <FontAwesomeIcon icon={faShare} />,
      modal: () => setShareAssignmentsModal(!shareAssignmentsModal)
    },
    {
      id: 5,
      name: 'Publish',
      tooltip: 'Publish an assignment as a Public Assignment (only one can be checked)',
      icon: <FontAwesomeIcon icon={faPaperPlane} />,
      modal: () => setPublishAssignmentsModal(!publishAssignmentsModal)
    },
    {
      id: 6,
      name: 'Submit',
      tooltip: 'Submit an assignment as a Benchmark (only one can be checked)',
      icon: <FontAwesomeIcon icon={faClipboardCheck} />,
      modal: () => setSubmitAssignmentsModal(!submitAssignmentsModal)
    },
    {
      id: 7,
      name: 'Duplicate',
      tooltip: 'Duplicate an assignment',
      icon: <FontAwesomeIcon icon={faPaste} />,
      modal: () => setDuplicateAssignmentsModal(!duplicateAssignmentsModal)
    },
    {
      id: 9,
      name: 'Delete',
      tooltip: 'Delete assignments (only unassigned assignments can be deleted)',
      icon: <FontAwesomeIcon icon={faTrashCan} />,
      modal: () => setDeleteAssignmentsModal(!deleteAssignmentsModal)
    }
  ];
  const { visibleButtons, visibleCountOnScreen, showMoreMenu } = useFloatingMenuVisibility(floatingMenu);

  const exportAssignmentMenu = async () => {
    try {
      const option = {
        skip: 0,
        take: 0,
        courseId: filters.courseId,
        folderId: filters.folderId,
        sortColumn: filters.sortColumn ?? null,
        sortDirection: filters.sortDirection ?? null,
        assignmentStatus: filters.assignmentStatus,
        assignedVia: filters.assignedVia,
        isCollapsed: Boolean(filters?.isCollapsed),
        searchText: filters.selectedKeyWord,
        assignmentIds: selectedAssignment.isAllSelected
          ? [-1]
          : selectedAssignment.assignedData.map((assignment) => assignment.assignmentId)
      };

      const blob = await exportAssignment(option).unwrap();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = 'CastleXMLAssignments.xml';
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    }
  };

  const floatingMenuHandler = async (value) => {
    if (selectedAssignment.assignedData.length > 0) {
      setLoadingMenuItemId(value.id);
      try {
        if (value.id !== 3) {
          if (value.id === 4) {
            getData.setSelectRecipient(true);
            await value.modal();
          } else {
            if (value.id === 5 || value.id === 6) {
              if (selectedAssignment.assignedData.length === 1) {
                if (value.id === 6 || value.id === 5) {
                  await validateSubmitAssignmentAsync(value);
                } else {
                  await value.modal();
                }
              } else {
                CustomToast('Please check only one assignment', 'error');
              }
            } 
            else if(value.id == 7){
              if(selectedAssignment.selectedCount > 20)
                CustomToast('You can only select up to 20 assignments', 'warning');
              else
                await value.modal();
            }else {
              await value.modal();
            }
          }
        } else {
          await exportAssignmentMenu();
        }
      } finally {
        setLoadingMenuItemId(null);
      }
    } else {
      CustomToast('You have not selected any assignments.', 'info');
    }
  };

  const validateSubmitAssignmentAsync = async (value) => {
    if (!isLoading) {
      setIsLoading(true);
      try {
        const options = {
          assignmentId: selectedAssignment.assignedData.map((assignment) => {
            return assignment.assignmentId;
          })
        };
        
        const result = await validateSubmitAssignment(options);
        if (!result.error) {
          value.modal();
        }
        else {
          CustomToast(`${result.error.data}`, 'warning');
        }
        setIsLoading(false);
        setIsDisabled(false);
      } catch (ex) {
        console.log(ex);
        setIsLoading(false);
        setIsDisabled(false);
      }
    }
  };

  const handleAssignAssignment = () => {
    if (selectedAssignment.isAllSelected) {
      setAssignAssignmentsModal(!assignAssignmentsModal);
    }
    else {
      if (
        selectedAssignment.assignedData.every(
          (assignment) => assignment.assignedVia === selectedAssignment.assignedData[0].assignedVia
        )
      ) {
        if (selectedAssignment.assignedData[0].assignedVia) {
          setAssignAssignmentsModal(!assignAssignmentsModal);
        } else {
          setShowAssignmentMethodModal(true);
        }
      } else {
        CustomToast(
          'One or more of the selected assignments have a different method of assigning.  Please only select assignments with the same "Assigned Via" method.',
          'info'
        );
      }
    }
  };

  async function handleSaveAssignmentMethod() {
    try {
      await saveAssignmentMethod({
        assignmentIds: selectedAssignment.assignedData.map((assignment) => {
          return assignment.assignmentId;
        }),
        assignedViaId: assignmentMethodRef.current.value
      }).unwrap();

      var tempAssignedData = selectedAssignment.assignedData.map((assignment) => {
        return { ...assignment, assignedVia: parseInt(assignmentMethodRef.current.value) };
      });
      setAssignedViaUpdate(true);
      setAssignedData(tempAssignedData);

      setShowAssignmentMethodModal(false);
      setAssignAssignmentsModal(true);
      CustomToast(
        `The assignment method${selectedAssignment.assignedData.length === 1 ? ' has' : 's have'
        } been updated`,
        'success'
      );
    } catch (ex) {
      CustomToast(
        `Something went wrong, could not update assignment method${selectedAssignment.assignedData.length === 1 ? '' : 's'
        }`,
        'error'
      );
      console.log(ex);
    }
  }
  return (
    <>
      <div className="floating-menu-in">
        {visibleButtons.map((value, index) => (
          <OverlayTrigger
            key={`float-top-${value.id}`}
            placement="top"
            overlay={
              <Tooltip id={`tooltip-top-${value.id}`} className="custom-tooltip">
                {loadingMenuItemId === value.id ? value.name + ' in progress...' : value.tooltip}
              </Tooltip>
            }>
            <button
              className={!showMoreMenu && index === visibleCountOnScreen - 1 ? 'floating-menu-last-visible' : ''}
              id={`floating-menu-${value.id}`}
              onClick={(e) => { e.currentTarget.blur(); floatingMenuHandler(value); }}
            >
              {loadingMenuItemId === value.id ?
                <FontAwesomeIcon icon={faSpinner} spin />
                :
                value.icon
              }
              <span className={loadingMenuItemId === value.id ? 'inprogress' : ''}>{value.name}</span>
            </button>
          </OverlayTrigger>
        ))}

        {showMoreMenu && <Dropdown>
          <Dropdown.Toggle className='floating-menu-last-visible'>
            <FontAwesomeIcon icon={faEllipsis} />
            More
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {visibleButtons.map((value, index) => (
              <Dropdown.Item
                key={`float-menu-${index}`}
                onClick={
                  selectedAssignment.assignedData.length > 0
                    ? value.id !== 3
                      ? value.modal
                      : () => exportAssignmentMenu()
                    : () => {
                      CustomToast('You have not selected any assignments.', 'info');
                    }
                }
                disabled={isDisabled}>
                {value.icon} {value.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>}

        {/* Float 1 */}
        {assignAssignmentsModal && (
          <AssignMenuModal
            modalData={assignAssignmentsModal}
            setModalData={setAssignAssignmentsModal}
            enableCreateLink={enableCreateLink}
          />
        )}
        {/* Float 1 */}
        {/* Float 2 */}
        {moveAssignmentsModal && (
          <MoveMenuModal
            modalData={moveAssignmentsModal}
            setModalData={setMoveAssignmentsModal}
            fromMoreActions={false}
            activityType={ActivityTypes.Assignment}
          />
        )}

        {/* Float 2 */}
        {/* Float 4 */}

        {shareAssignmentsModal && 
        <ShareMenuModal
          assignmentId={selectedAssignment?.assignedData?.map((item) => item.assignmentId)}
          fromMoreAction={false}
          isModalOpen={setShareAssignmentsModal}
        />

        }
        {/* Float 4 */}
        {/* Float 5 */}
        <PublishMenuModal
          modalData={publishAssignmentsModal}
          setModalData={setPublishAssignmentsModal}
          fromMoreActions={false}
          activityType={ActivityTypes.Assignment}
        />
        {/* Float 5 */}
        {/* Float 6 */}
        <SubmitMenuModal
          modalData={submitAssignmentsModal}
          setModalData={setSubmitAssignmentsModal}
          fromMoreActions={false}
        />
        {/* Float 6 */}
        {/* Float 7 */}
        {duplicateAssignmentsModal &&
        <DuplicateMenuModal
          modalData={duplicateAssignmentsModal}
          setModalData={setDuplicateAssignmentsModal}
          fromMoreActions={false}
          activityType={ActivityTypes.Assignment}
        />

        }

        {/* Float 8 */}
        {/* Float 9 */}
        {deleteAssignmentsModal && (
          <DeleteMenuModal
            modalData={deleteAssignmentsModal}
            setModalData={setDeleteAssignmentsModal}
            fromMoreActions={false}
            activityType={ActivityTypes.Assignment}
          />
        )}
        {/* Float 9 */}
      </div>

      <Modal
        contentWidth="40%"
        isOpen={showAssignmentMethodModal}
        titleName={'Assignment Method'}
        onClose={() => {
          setShowAssignmentMethodModal(false);
        }}
        disableBackdropClose={true}>
        <div className="d-flex flex-column align-items-center">
          <div
            className="mb-3"
            style={{
              backgroundImage: `url(${assignMethodImg})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100%',
              height: '130px',
              width: '199px'
            }}></div>
          <div>
            {selectedAssignment.assignedData.length === 1
              ? 'How will you assign this assignment?'
              : 'How will you assign these assignments'}
          </div>
          <div className="d-flex flex-row w-50 my-4 align-items-center justify-content-evenly">
            <span>Assigned Via</span>
            <Form.Select ref={assignmentMethodRef} style={{ maxWidth: '50%' }} defaultValue="1">
              {[
                { assignedViaId: 1, name: 'Castle' },
                { assignedViaId: 2, name: 'Google Classroom' },
                { assignedViaId: 3, name: 'Schoology' },
                { assignedViaId: 4, name: 'Canvas' }
              ].map((item, index) => {
                return (
                  <option key={`assignmentMethodKey${index}`} value={item.assignedViaId}>
                    {item.name}
                  </option>
                );
              })}
            </Form.Select>
          </div>
          <div className="d-flex flex-row w-50 justify-content-evenly">
            <Button
              variant="castleGray"
              style={{ width: '45%' }}
              onClick={() => {
                setShowAssignmentMethodModal(false);
              }}>
              Cancel
            </Button>
            <Button
              variant="castleRed"
              style={{ width: '45%' }}
              onClick={(e) => handleSaveAssignmentMethod(e)}
              disabled={saveAssignmentMethodResults.isLoading}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

const Layout = () => {
  return (
    <DashboardProvider>
      <AssignmentFloatingMenu />
    </DashboardProvider>
  );
};

export default Layout;
